import {type CopilotPersona, personaIds} from './personas'

export interface PersonaTask {
  id: string
  title: string
  description: string
  prompt: string
}

export const personaTasks: Record<CopilotPersona, readonly [PersonaTask, PersonaTask, PersonaTask]> = {
  [personaIds.softwareDevelopmentIt]: [
    {
      id: 'investigate-issue',
      title: 'Investigate an issue',
      description: 'Find the likely cause and recommend a fix',
      prompt:
        "Investigate this issue: [paste an issue link or describe the problem]. Identify and explain the root cause and propose a concise fix. Don't make changes yet.",
    },
    {
      id: 'understand-repository',
      title: 'Understand a repository',
      description: 'Explore its purpose, structure, and key files.',
      prompt:
        "Explain this repository's purpose, architecture, main entry points, and dependencies. Recommend which files I should explore first.",
    },
    {
      id: 'explore-copilot-software-development-it',
      title: 'Explore Copilot',
      description: 'Discover practical ways to apply Copilot to your workflow.',
      prompt: `I work in software development or IT. Help me discover how GitHub Copilot can fit my specific workflow.
Start a guided conversation by asking me one short question at a time, up to five questions about:
- My role and responsibilities
- My technical experience
- My tools and technology stack
- Repetitive work or current challenges
- What I want to improve
Wait for my response after each question.
Then recommend three tailored ways Copilot could help. For each, provide:
- Why it fits my workflow
- A practical task I can try
- An exact starter prompt
- The context or access Copilot needs
- The expected outcome
Recommend the easiest high-value task to try first. Avoid generic suggestions and don't make changes without my confirmation.`,
    },
  ],
  [personaIds.productDesignResearch]: [
    {
      id: 'create-project-brief',
      title: 'Create a project brief',
      description: 'Turn an idea or notes into a clear plan',
      prompt: `Help me turn this idea or these notes into a clear brief: [paste or attach your content].
First, ask what type of work this is (product, design, or research) and who the brief is for. Wait for my answer. Then draft:
- The problem or opportunity
- The audience
- The desired outcome
- Scope and requirements
- Assumptions and open questions
- Success signals
- Recommended next steps
Clearly label anything you inferred so I can easily edit it.`,
    },
    {
      id: 'synthesize-feedback',
      title: 'Synthesize feedback',
      description: 'Find key themes, evidence, and next steps.',
      prompt: `Analyze this feedback: [paste or attach research notes, survey responses, support feedback, or a transcript].

First, summarize it into 3-5 themes with supporting evidence, contradictions, and unanswered questions. Don't invent quotes or conclusions. Then ask what decision I'm trying to make. After I answer, recommend prioritized opportunities and next steps.`,
    },
    {
      id: 'explore-copilot-product-design-research',
      title: 'Explore Copilot',
      description: 'Discover practical ways to apply Copilot to your workflow.',
      prompt: `I work in product, design, or UX research. Help me discover how GitHub Copilot can fit my specific workflow.
Ask me one short question at a time, up to five questions about:
- My role and responsibilities
- My experience and comfort with technical tools
- My current project and its stage
- The tools and artifacts I work with
- Repetitive tasks, challenges, or areas I want to improve
Wait for each answer before continuing.
Then recommend three tailored ways Copilot could help. For each, provide:
- Why it fits my workflow
- A lightweight task I can try
- An exact starter prompt
- The context, files, or repository access needed
- The expected outcome
Recommend the easiest high-value task first. Avoid generic suggestions and don't take action without my confirmation.`,
    },
  ],
  [personaIds.dataAnalytics]: [
    {
      id: 'explore-dataset',
      title: 'Explore a dataset',
      description: 'Find data-quality issues, patterns, and questions to investigate.',
      prompt:
        'Help me explore this data: [attach a file, paste a sample, or describe the data source]. First, summarize its structure and flag missing context or data-quality issues. Then identify 3-5 useful patterns or anomalies and suggest questions worth investigating.',
    },
    {
      id: 'build-dashboard',
      title: 'Build a dashboard',
      description: 'Turn existing data into a focused, interactive dashboard.',
      prompt:
        'Build a simple interactive dashboard from this data: [attach, paste, or describe your data]. First, propose the 3-5 most useful metrics and chart types. Wait for my approval, then build an initial version I can refine.',
    },
    {
      id: 'explore-copilot-data-analytics',
      title: 'Explore Copilot',
      description: 'Discover practical ways to apply Copilot to your workflow.',
      prompt: `I work in data and analytics. Help me discover how GitHub Copilot can fit my specific workflow.
Ask me one short question at a time, up to five questions about:
- My role and responsibilities
- My experience with data and technical tools
- The tools, languages, and data sources I use
- The analyses, dashboards, models, or reports I create
- Repetitive tasks, challenges, or areas I want to improve
Wait for each answer before continuing.
Then recommend three tailored ways Copilot could help. For each, provide:
- Why it fits my workflow
- A lightweight task I can try
- An exact starter prompt
- The context, files, or data access needed
- The expected outcome
Recommend the easiest high-value task first. Avoid generic suggestions, don't request sensitive data, and don't take action without my confirmation.`,
    },
  ],
  [personaIds.businessOperations]: [
    {
      id: 'create-competitive-brief',
      title: 'Create a competitive brief',
      description: 'Compare positioning, strengths, and opportunities.',
      prompt:
        'Create a structured competitive brief for [company or product]. Compare positioning, audience, strengths, weaknesses, and important opportunities.',
    },
    {
      id: 'analyze-spreadsheet',
      title: 'Analyze a spreadsheet',
      description: 'Find patterns, anomalies, and recommended actions.',
      prompt:
        'Analyze this spreadsheet: [attach your file and optionally describe your goal or decision]. First, summarize its structure and flag any data-quality issues. Then identify important patterns and anomalies, and provide a concise summary with recommended actions.',
    },
    {
      id: 'explore-copilot-business-operations',
      title: 'Explore Copilot',
      description: 'Discover practical ways to apply Copilot to your workflow.',
      prompt: `I work in business and operations. Help me discover how GitHub Copilot can fit my specific workflow.
Ask me one short question at a time, up to five questions about:
- My role and responsibilities
- The business processes or recurring tasks I manage
- The tools, documents, and systems I use
- The teams I work with and where handoffs get difficult
- The decisions, bottlenecks, or outcomes I want to improve
Wait for each answer before continuing.
Then recommend three tailored ways Copilot could help. For each, provide:
- Why it fits my workflow
- A lightweight task I can try
- An exact starter prompt
- The context, files, or data access needed
- The expected outcome
Recommend the easiest high-value task first. Avoid generic suggestions, don't request sensitive data, and don't take action without my confirmation.`,
    },
  ],
  [personaIds.learningTeaching]: [
    {
      id: 'create-learning-plan',
      title: 'Create a learning plan',
      description: 'Turn a learning goal into practical milestones and exercises.',
      prompt: `Create a learning plan for [topic] for [me or my learners]. The current experience level is [level], the goal is [desired outcome], and the available time is [hours per week or deadline].
If important details are missing, ask up to three short questions first. Then provide:
- Clear, achievable milestones
- A practical exercise for each milestone
- Recommended resources
- Ways to check progress
- A small project to apply the learning
- One task that can be started today
Keep the plan realistic and appropriate for the stated experience level.`,
    },
    {
      id: 'explain-file-or-concept',
      title: 'Explain a file or concept',
      description: 'Get a clear, step-by-step explanation at your level.',
      prompt: `Help [me or my learners] understand [attach a file or enter a concept]. Explain it at a [beginner, intermediate, or advanced] level.
Start with a short overview, then:
- Explain it step by step
- Define unfamiliar terms
- Use simple, practical examples
- Show how the important parts connect
- Highlight common mistakes or misconceptions
- Finish with a short summary and a practice question`,
    },
    {
      id: 'explore-copilot-learning-teaching',
      title: 'Explore Copilot',
      description: 'Discover practical ways to apply Copilot to your workflow.',
      prompt: `I am learning, teaching, or changing my career. Help me discover how GitHub Copilot can fit my specific workflow.
Ask me one short question at a time, up to five questions about:
- Whether I am learning, teaching, or changing careers, and my goals
- My current knowledge or my learners' experience level
- The topics, curriculum, or content I work with
- The tools, materials, and learning or teaching formats I use
- The challenges, time constraints, or learner needs I want to address
Wait for each answer before continuing.
Then recommend three tailored ways Copilot could help. For each, provide:
- Why it fits my workflow
- A lightweight task I can try
- An exact starter prompt
- The context, files, or data access needed
- The expected outcome
Recommend the easiest high-value task first. Avoid generic suggestions, don't request sensitive data, and don't take action without my confirmation.`,
    },
  ],
  [personaIds.other]: [
    {
      id: 'synthesize-my-data',
      title: 'Synthesize my data',
      description: 'Find key patterns and takeaways backed by evidence.',
      prompt:
        'Analyze this and tell me what matters: [paste data, feedback, notes, or add a spreadsheet]. Find the key patterns, surprises, and takeaways, and back each one with the evidence.',
    },
    {
      id: 'understand-project',
      title: 'Understand a project',
      description: 'Learn the basics and how to get hands-on.',
      prompt:
        'Explain how [a tool, dataset, codebase, or process] works, starting from the basics, then show me how to get hands-on with it.',
    },
    {
      id: 'automate-recurring-task',
      title: 'Automate a recurring task',
      description: 'Identify what can be automated and plan the implementation.',
      prompt:
        'Help me automate a recurring task: [describe the process]. Map the current steps, flag what can be automated, and give me an implementation plan.',
    },
  ],
}
